// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";
import merge from "lodash/merge";

interface AddressesPageState {
    oneTimeAddress?: ShipToModel;
    isSaveOneTimeAddress?: boolean;
}

interface CreditCardBillingAddress {
    address1: string;
    country: string;
    state: string;
    city: string;
    postalCode: string;
}

interface PaymentPageState {
    creditCardBillingAddress?: CreditCardBillingAddress;
    isSavedPayment?: boolean;
    savedPaymentDescription?: string;
    poNumber?: string;
    firstSix?: string;
    lastFour?: string;
    cardType?: string;
    isCreditCard?: boolean;
    paymentMethod?: string;
    expirationDate?: string;
}

interface ShippingState {
    requestedDate?: Date;
}
interface PickupState {
    requestedDate?: Date;
}
interface FulfillmentState {
    shipping?: ShippingState;
    pickup?: PickupState;
}
interface ChangingShipToAddressState {
    isChangingShipToAddress?: boolean;
}

export interface XCheckoutStateType {
    addresses: AddressesPageState;
    payment: PaymentPageState;
    fulfillment: FulfillmentState;
    isChangingShipToAddress?: boolean;
}

export const initialCheckoutState: XCheckoutStateType = {
    addresses: {},
    payment: {},
    fulfillment: {},
    isChangingShipToAddress: undefined,
};

const reducer = {
    "Pages/CheckoutShipping/SetShippingAddress": (
        draft: Draft<XCheckoutStateType>,
        action: { address: ShipToModel },
    ) => {
        if (!action.address.oneTimeAddress) {
            return;
        }
        draft.addresses.oneTimeAddress = action.address;
    },
    "xCheckout/Update": (draft: Draft<XCheckoutStateType>, action: { data: Partial<XCheckoutStateType> }) => {
        Object.keys(action.data).forEach(key => {
            if (key === "isChangingShipToAddress") {
                draft[key] = action.data[key];
                return;
            }
            const tKey = key as keyof Omit<XCheckoutStateType, "isChangingShipToAddress">;
            const newObj = merge(draft[tKey], action.data[tKey]);
            draft[tKey] = newObj;
        });
    },
    "xCheckout/Reset": (
        draft: Draft<XCheckoutStateType>,
        action: { path?: keyof Omit<XCheckoutStateType, "isChangingShipToAddress"> },
    ) => {
        if (!action.path) {
            Object.keys(initialCheckoutState).forEach(key => {
                if (key === "isChangingShipToAddress") {
                    draft[key] = undefined;
                    return;
                }
                const tKey = key as keyof Omit<XCheckoutStateType, "isChangingShipToAddress">;
                draft[tKey] = {};
            });
            return;
        }
        if ((action.path as any) === "isChangingShipToAddress") {
            draft.isChangingShipToAddress = undefined;
            return;
        }
        draft[action.path] = {};
    },
    "xCheckout/IsChangingShipToAddress": (
        draft: Draft<XCheckoutStateType>,
        action: { isChangingShipToAddress: boolean },
    ) => {
        draft.isChangingShipToAddress = action.isChangingShipToAddress;
    },
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: XCheckoutStateType;
} & ApplicationState;

type xCheckoutReducers = {
    [key: string]: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
